/**
 * QuoteConvertedBanner — the approval-section banner for a quote in Status
 * 'Converted' (G2-3). Split out of QuoteApprovalSection (2026-09-11).
 *
 * 2026-08-06 (Vahid: "I can't tell which project the WO is assigned to"): the
 * banner names the WO AND its project explicitly, and links there.
 *
 * 2026-09-11 (Vahid: "deleting the WO should release the quote"): the
 * quote → WO link is now explicit (Quote.ConvertedToWorkOrderKey) and the WO
 * delete cascade releases the quote back to Accepted. A legacy quote left
 * 'Converted' pointing at an already-deleted work order is rendered
 * TRUTHFULLY — amber, "that work order has since been deleted", no "Open Work
 * Order" button — instead of as a live conversion. Its status is left alone
 * here (repairing those rows is a separate, held decision).
 */
import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { PostAdd, ReportProblemOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import type { ConvertedTarget } from '../../../services/quoteService';
import type { QuoteEvent } from '../../../types/quote';

interface Props {
  /** Shared banner frame from the approval section (padding, border, radius). */
  baseSx: SxProps<Theme>;
  /** Resolved WO + project, or null while loading / unresolvable. */
  target: ConvertedTarget | null;
  /** The 'Converted' QuoteEvent — its Details text is the fallback wording. */
  convertEvent: QuoteEvent | undefined;
}

export function QuoteConvertedBanner({ baseSx, target, convertEvent }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const deleted = target?.workOrderDeleted === true;
  const tone = deleted ? theme.palette.warning.main : theme.palette.success.main;
  const Icon = deleted ? ReportProblemOutlined : PostAdd;

  const woLabel = target ? (
    <>
      work order <strong>{target.workOrderId}</strong>
      {target.workOrderName ? ` — ${target.workOrderName}` : ''} in project{' '}
      <strong>{target.projectId}{target.projectName ? ` — ${target.projectName}` : ''}</strong>
    </>
  ) : null;

  return (
    <Box sx={{ ...(baseSx as object), backgroundColor: alpha(tone, 0.06) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Icon sx={{ color: tone, fontSize: 20 }} />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>
          {target && deleted && (
            <>
              This quote was converted to {woLabel}, but <strong>that work order has since been
              deleted</strong>. The quote still shows as Converted; its schedule was released with
              the work order.
            </>
          )}
          {target && !deleted && (
            <>
              This quote was converted to {woLabel}. Its schedule now belongs to the work order.
            </>
          )}
          {!target && (
            <>
              This quote was <strong>converted to a work order</strong>
              {convertEvent?.Details ? ` — ${convertEvent.Details}` : '.'} Its schedule
              now belongs to the work order.
            </>
          )}
        </Typography>
        {target && !deleted && (
          <Button
            variant="outlined"
            size="small"
            color="success"
            sx={{ textTransform: 'none', fontWeight: 600 }}
            onClick={() => navigate(
              ROUTES.WORK_ORDER_DETAIL
                .replace(':projectId', String(target.projectId))
                .replace(':workOrderId', target.workOrderKey),
            )}
          >
            Open Work Order
          </Button>
        )}
      </Box>
    </Box>
  );
}
